import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import LooperEngine from '../audio/LooperEngine';

// React-facing hook for the LooperEngine. Owns a single engine
// instance per component lifetime, subscribes to its snapshot via
// useSyncExternalStore, tears down on unmount.
//
// Usage:
//   const { snapshot, engine } = useLooperEngine({ fetchAudioData });
//   if (snapshot.isPlaying) ...
//   <button onClick={engine.play}>Play</button>
//
// The engine instance is stable - the same object reference for the
// entire lifetime of the hook - so callers can pass engine.play /
// engine.stop / engine.setVolume(id) directly into onClick without
// triggering re-renders.

export const useLooperEngine = ({ fetchAudioData, audioContextFactory } = {}) => {
  const optionsRef = useRef({ fetchAudioData, audioContextFactory });
  // Keep the latest fetch fn so the engine always calls the current
  // one (it captures the ref, not the value).
  useEffect(() => {
    optionsRef.current.fetchAudioData = fetchAudioData;
    optionsRef.current.audioContextFactory = audioContextFactory;
  }, [fetchAudioData, audioContextFactory]);

  const engine = useMemo(() => new LooperEngine({
    fetchAudioData: (id) => optionsRef.current.fetchAudioData(id),
    audioContextFactory: () => (optionsRef.current.audioContextFactory
      ? optionsRef.current.audioContextFactory()
      : new (window.AudioContext || window.webkitAudioContext)()),
  }), []);

  const snapshot = useSyncExternalStore(engine.subscribe, engine.getSnapshot);

  // Engine teardown on unmount
  useEffect(() => {
    return () => {
      engine.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { engine, snapshot };
};

export default useLooperEngine;
